import { Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { Chart, ChartConfiguration, ChartData, Plugin } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { TempDataPoint, SolarHistoryPoint } from '../version.service';
import { chartPalette, timeTooltipTitle, lineLegendLabels, dragZoom, relativeDayTick, timeAxisTick } from '../shared/chart.utils';

const crosshairPlugin: Plugin<'line'> = {
  id: 'crosshairLine',
  afterDraw(chart) {
    const active = (chart as any).tooltip?._active;
    if (!active?.length) return;
    const x    = active[0].element.x;
    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.lineWidth    = 1;
    ctx.strokeStyle  = 'rgba(128,128,128,0.5)';
    ctx.setLineDash([4, 3]);
    ctx.stroke();
    ctx.restore();
  },
};
Chart.register(crosshairPlugin);

@Component({
  selector: 'app-sparkline-chart',
  template: `
    <div [style.height]="expanded ? 'auto' : '90px'"
         [style.aspect-ratio]="expanded ? '16/9' : null"
         style="position:relative;">
      <canvas baseChart
        [data]="chartData"
        [options]="chartOptions"
        type="line">
      </canvas>
      <button *ngIf="zoomed" class="btn btn-sm btn-outline-secondary"
              style="position:absolute;top:4px;right:4px;font-size:.6rem;padding:1px 5px;opacity:.85;z-index:2;"
              (click)="resetZoom()">↩ Reset</button>
    </div>
  `,
})
export class SparklineChartComponent implements OnChanges {
  @ViewChild(BaseChartDirective) chartDirective?: BaseChartDirective;
  @Input() points: TempDataPoint[] = [];
  @Input() darkMode = false;
  @Input() expanded = false;
  @Input() mode: 'temp' | 'humidity' = 'temp';

  @Input() solarPoints: SolarHistoryPoint[] = [];
  @Input() solarMode: 'pvYield' | 'battery' | 'grid' | 'consumption' | null = null;
  /** Visible x window in ms (now-windowMs … now). 24h → relative ticks; longer → date ticks. */
  @Input() windowMs = 24 * 3600 * 1000;

  chartData: ChartData<'line'> = { datasets: [] };
  chartOptions: ChartConfiguration<'line'>['options'] = {};
  zoomed = false;

  resetZoom(): void {
    (this.chartDirective?.chart as any)?.resetZoom();
    this.zoomed = false;
  }

  private fmtW(w: number): string {
    const abs = Math.abs(w);
    if (abs >= 1000) return (w / 1000).toFixed(1) + ' kW';
    return w.toFixed(0) + ' W';
  }

  ngOnChanges(_changes: SimpleChanges): void {
    const { textColor, gridColor } = chartPalette(this.darkMode);

    // x window spans the selected range (now-windowMs … now), so date labels show
    // across a Woche/Monat even when the archive is still sparse. 24h → relative ticks.
    const xMax = Date.now();
    const xMin = xMax - this.windowMs;
    const xTick = this.windowMs > 2 * 24 * 3600 * 1000 ? timeAxisTick(xMin, xMax) : relativeDayTick;

    if (this.solarMode !== null) {
      const solarColors: Record<string, string> = {
        pvYield:     '#ffc107',
        battery:     '#198754',
        grid:        '#0dcaf0',
        consumption: '#0d6efd',
      };
      const getValue = (p: SolarHistoryPoint): number => {
        switch (this.solarMode) {
          case 'pvYield':     return p.pvYieldW;
          case 'battery':     return -p.batteryDischargeW;
          case 'grid':        return p.gridConsumptionW;
          case 'consumption': return p.totalConsumptionW;
          default:            return 0;
        }
      };
      const solarYLabel: Record<string, string> = {
        pvYield:     'Erzeugung (W)',
        battery:     'Batterie (W)',
        grid:        'Netzbezug (W)',
        consumption: 'Verbrauch (W)',
      };
      const miniFont = { size: this.expanded ? 11 : 10 };
      const isBattery = this.solarMode === 'battery';
      const isConsumption = this.solarMode === 'consumption';
      const hasPhases = isConsumption && this.solarPoints.some(p =>
        (p.consumptionL1W ?? 0) > 0 || (p.consumptionL2W ?? 0) > 0 || (p.consumptionL3W ?? 0) > 0);
      const showPhases = hasPhases && this.expanded;
      const showLegend = (isBattery || showPhases) && this.expanded;
      this.chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: 'index', intersect: false },
        layout: { padding: { top: 4, right: isBattery ? 0 : 6, bottom: 0, left: 0 } },
        plugins: {
          legend: showLegend
            ? { display: true, labels: lineLegendLabels(textColor) }
            : { display: false },
          tooltip: {
            callbacks: {
              title: timeTooltipTitle,
              label: (item) => (item.dataset as any).yAxisID === 'y1'
                ? ` ${Math.round(item.parsed.y ?? 0)} %`
                : ` ${this.fmtW(item.parsed.y ?? 0)}`,
            },
          },
          ...(this.expanded ? { zoom: dragZoom(() => { this.zoomed = true; }) } : {}),
        },
        scales: {
          x: {
            display: true,
            type: 'linear',
            min: xMin,
            max: xMax,
            ticks: {
              color: textColor,
              maxTicksLimit: this.expanded ? 8 : 3,
              font: miniFont,
              maxRotation: 0,
              callback: xTick,
            },
            grid: { display: this.expanded, color: gridColor },
            title: { display: this.expanded, text: 'Zeit (24h)', color: textColor, font: miniFont },
          },
          y: {
            display: true,
            ticks: {
              color: textColor,
              maxTicksLimit: this.expanded ? 6 : 4,
              font: miniFont,
              callback: (val) => this.fmtW(val as number),
            },
            grid: { color: gridColor, display: this.expanded },
            title: {
              display: this.expanded,
              text: this.solarMode ? solarYLabel[this.solarMode] ?? 'Watt (W)' : 'Watt (W)',
              color: textColor,
              font: miniFont,
            },
          },
          ...(isBattery ? {
            y1: {
              position: 'right' as const,
              min: 0,
              max: 100,
              display: true,
              ticks: {
                color: textColor,
                font: miniFont,
                maxTicksLimit: this.expanded ? 6 : 4,
                callback: (val: any) => `${val} %`,
              },
              grid: { drawOnChartArea: false },
            },
          } : {}),
        },
        elements: {
          point: { radius: 0, hoverRadius: 4 },
          line:  { borderWidth: this.expanded ? 2 : 1.5, tension: 0.4 },
        },
      };
      if (showPhases) {
        const phaseColors = ['#0d6efd', '#6610f2', '#d63384'];
        const phaseLabels = ['L1', 'L2', 'L3'];
        const phaseGetters = [
          (p: SolarHistoryPoint) => p.consumptionL1W ?? 0,
          (p: SolarHistoryPoint) => p.consumptionL2W ?? 0,
          (p: SolarHistoryPoint) => p.consumptionL3W ?? 0,
        ];
        const xMin = Date.now() - 24 * 3600 * 1000;
        const xMax = Date.now();
        const datasets: any[] = phaseGetters.map((fn, i) => ({
          label: phaseLabels[i],
          data: this.solarPoints.map(p => ({ x: p.ts, y: fn(p) })),
          borderColor: phaseColors[i],
          backgroundColor: 'transparent',
          fill: false,
          borderWidth: 2,
        }));
        datasets.push({
          label: 'Gesamt',
          data: this.solarPoints.map(p => ({ x: p.ts, y: p.totalConsumptionW })),
          borderColor: 'rgba(108,117,125,0.5)',
          backgroundColor: 'transparent',
          fill: false,
          borderWidth: 1,
          borderDash: [4, 3],
        });
        // Grid barrier line (16A = 3680W)
        datasets.push({
          label: '16A Grid (3680W)',
          data: [{ x: xMin, y: 3680 }, { x: xMax, y: 3680 }],
          borderColor: 'rgba(255,165,0,0.6)',
          backgroundColor: 'transparent',
          fill: false,
          borderWidth: 1.5,
          borderDash: [6, 4],
          pointRadius: 0,
          pointHoverRadius: 0,
        });
        // Battery barrier line (32A = 7360W)
        datasets.push({
          label: '32A Batt (7360W)',
          data: [{ x: xMin, y: 7360 }, { x: xMax, y: 7360 }],
          borderColor: 'rgba(255,0,0,0.5)',
          backgroundColor: 'transparent',
          fill: false,
          borderWidth: 1.5,
          borderDash: [6, 4],
          pointRadius: 0,
          pointHoverRadius: 0,
        });

        this.chartData = { datasets };
        return;
      }

      const datasets: any[] = [{
        label: isBattery ? 'W' : undefined,
        data: this.solarPoints.map(p => ({ x: p.ts, y: getValue(p) })),
        borderColor: solarColors[this.solarMode] ?? '#6c757d',
        backgroundColor: 'transparent',
        fill: false,
      }];
      if (isBattery) {
        datasets.push({
          label: '%',
          data: this.solarPoints.map(p => ({ x: p.ts, y: p.batteryPercent })),
          borderColor: '#a855f7',
          backgroundColor: 'transparent',
          fill: false,
          yAxisID: 'y1',
          borderDash: [4, 3],
        });
      }
      this.chartData = { datasets };
      return;
    }

    const miniFont = { size: this.expanded ? 11 : 10 };
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      layout: { padding: { top: 4, right: 6, bottom: 0, left: 0 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: timeTooltipTitle,
            label: (item) => this.mode === 'humidity'
              ? ` ${(item.parsed.y ?? 0).toFixed(0)} %`
              : ` ${(item.parsed.y ?? 0).toFixed(1)} °C`,
          },
        },
        ...(this.expanded ? { zoom: dragZoom(() => { this.zoomed = true; }) } : {}),
      },
      scales: {
        x: {
          display: true,
          type: 'linear',
          min: xMin,
          max: xMax,
          ticks: {
            color: textColor,
            maxTicksLimit: this.expanded ? 8 : 3,
            font: miniFont,
            maxRotation: 0,
            callback: xTick,
          },
          grid: { display: this.expanded, color: gridColor },
          title: { display: this.expanded, text: 'Zeit (24h)', color: textColor, font: miniFont },
        },
        y: {
          display: true,
          ticks: {
            color: textColor,
            maxTicksLimit: this.expanded ? 6 : 4,
            font: miniFont,
            callback: (val) => this.mode === 'humidity'
              ? `${(val as number).toFixed(0)} %`
              : `${(val as number).toFixed(1)} °`,
          },
          grid: { color: gridColor, display: this.expanded },
          title: {
            display: this.expanded,
            text: this.mode === 'humidity' ? 'Luftfeuchtigkeit (%)' : 'Temperatur (°C)',
            color: textColor,
            font: miniFont,
          },
        },
      },
      elements: {
        point: { radius: 0, hoverRadius: 4 },
        line:  { borderWidth: this.expanded ? 2 : 1.5, tension: 0.6 },
      },
    };

    const isHumidity = this.mode === 'humidity';
    this.chartData = {
      datasets: [{
        data: this.points
          .filter(p => !isHumidity || p.humidity != null)
          .map(p => ({ x: p.ts, y: isHumidity ? (p.humidity as number) : p.tempC })),
        borderColor: isHumidity ? '#0dcaf0' : '#0d6efd',
        backgroundColor: 'transparent',
        fill: false,
      }],
    };
  }
}
